import { ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import useDatasets from "datasets/hooks/useDatasets";
import AccentColor from "datasets/types/enums/accent-color";
import Dataset from "datasets/types/interfaces/dataset";
import { Plus } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { ROUTE_NEXTGEN_DATASETS } from "~/router/constants";
import { apiMutate } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

interface DatasetForm {
  title: string;
  description: string;
}

interface CreateDatasetModalProps {
  trigger: ReactNode;
}

export function CreateDatasetModal({ trigger }: CreateDatasetModalProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { mutate } = useDatasets();
  const [error, setError] = useState("");
  const form = useForm<DatasetForm>({
    defaultValues: { title: "", description: "" },
    mode: "onChange",
  });

  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      setOpen(false);
      form.reset();
      setError("");
    }
  };

  const onSubmit = async (data: DatasetForm) => {
    try {
      const datasetResp = await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets`, "POST", {
        title: data.title,
        description: data.description,
        accentColor: AccentColor.PURPLE,
        createSchemaEnabled: true,
      });
      trackEvent("dataset_created", {
        dataset_description: datasetResp.description,
        dataset_id: datasetResp.id,
        dataset_title: datasetResp.title,
        dataset_created_by: datasetResp.createdBy,
      });
      setOpen(false);
      navigate(`${ROUTE_NEXTGEN_DATASETS}/${datasetResp.id}`);
      mutate();
    } catch {
      setError("Unable to create dataset");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New Dataset</DialogTitle>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="title" className="gap-0">
              Title
              <span className="text-destructive" aria-hidden="true">
                *
              </span>
            </Label>
            <Controller
              control={form.control}
              name="title"
              rules={{ required: { value: true, message: "Required" } }}
              render={({ field, formState }) => (
                <Input
                  {...field}
                  id="title"
                  disabled={formState.isSubmitting}
                  placeholder="Enter dataset title..."
                  autoComplete="off"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="description">Description</Label>
            <Controller
              control={form.control}
              name="description"
              render={({ field, formState }) => (
                <Textarea
                  {...field}
                  id="description"
                  disabled={formState.isSubmitting}
                  rows={5}
                  placeholder="Describe this dataset..."
                />
              )}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={form.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isValid}
              loading={form.formState.isSubmitting}
            >
              <Plus aria-hidden="true" />
              Create Dataset
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
